/**
 * Double booking detection for employees
 */

import type { Task } from "../../types";
import type { ScheduleResult, DoubleBookingWarning } from "./types";

/**
 * Erkennt Doppelbelegungen: Personen die gleichzeitig mehreren Tasks zugewiesen sind.
 */
export function detectDoubleBookings(
  tasks: Task[],
  schedule: ScheduleResult,
  employees: { id: string; name: string }[]
): DoubleBookingWarning[] {
  const warnings: DoubleBookingWarning[] = [];
  const employeeMap = new Map(employees.map(e => [e.id, e.name]));

  // Sammle Tasks pro Mitarbeiter
  const tasksByEmployee = new Map<string, {
    taskId: string;
    taskTitle: string;
    column: string;
    startDay: number;
    endDay: number;
  }[]>();

  for (const [taskId, scheduled] of schedule.scheduledTasks) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) continue;

    for (const empId of scheduled.assigneeIds) {
      if (!tasksByEmployee.has(empId)) {
        tasksByEmployee.set(empId, []);
      }
      tasksByEmployee.get(empId)!.push({
        taskId,
        taskTitle: task.title,
        column: scheduled.column,
        startDay: scheduled.startDay,
        endDay: scheduled.endDay,
      });
    }
  }

  // Prüfe für jeden Mitarbeiter auf Überlappungen
  for (const [empId, empTasks] of tasksByEmployee) {
    if (empTasks.length < 2) continue;

    const sortedTasks = [...empTasks].sort((a, b) => a.startDay - b.startDay);

    // Finde überlappende Gruppen
    const overlappingGroups: typeof empTasks[] = [];
    let currentGroup = [sortedTasks[0]];

    for (let i = 1; i < sortedTasks.length; i++) {
      const task = sortedTasks[i];
      const overlapsWithGroup = currentGroup.some(
        groupTask => task.startDay <= groupTask.endDay && task.endDay >= groupTask.startDay
      );

      if (overlapsWithGroup) {
        currentGroup.push(task);
      } else {
        if (currentGroup.length > 1) {
          overlappingGroups.push(currentGroup);
        }
        currentGroup = [task];
      }
    }

    if (currentGroup.length > 1) {
      overlappingGroups.push(currentGroup);
    }

    for (const group of overlappingGroups) {
      const overlapStartDay = Math.max(...group.map(t => t.startDay));
      const overlapEndDay = Math.min(...group.map(t => t.endDay));

      warnings.push({
        employeeId: empId,
        employeeName: employeeMap.get(empId) || empId,
        tasks: group,
        overlapStartDay,
        overlapEndDay,
      });
    }
  }

  return warnings;
}
